import i18n from '@/i18n'
import { ElMessageBox } from 'element-plus'
const { t } = i18n.global

// 扩展名 -> 允许的 MIME 类型
const FILE_MIME_MAP: Record<string, string[]> = {
  png: ['image/png'],
  jpg: ['image/jpeg'],
  jpeg: ['image/jpeg'],
  gif: ['image/gif'],
  bmp: ['image/bmp', 'image/x-ms-bmp'],
  webp: ['image/webp'],
  txt: ['text/plain'],
  xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  xls: ['application/vnd.ms-excel']
}

// 取扩展名（最后一个点之后，转小写）
const getExtension = (fileName?: string) => {
  if (!fileName) return ''
  const idx = fileName.lastIndexOf('.')
  return idx === -1 ? '' : fileName.slice(idx + 1).toLowerCase()
}

// 检查文件类别：后缀 + MIME 两层校验。
// @param file File 对象（优先）；兼容传入纯文件名字符串（仅做后缀校验）
// @param types 允许的扩展名列表，如 ['png', 'jpg']
export const checkFileType = (file: File | string, types: string[]): boolean => {
  if (!types || !types.length) return true // 未配置白名单则放行
  const allowed = types.map(t => t.toLowerCase())

  if (typeof file === 'string') {
    // 兼容旧调用：仅校验后缀
    return allowed.includes(getExtension(file))
  }

  const name = file.name || ''
  const ext = getExtension(name)
  // 1) 后缀必须在白名单内
  if (!allowed.includes(ext)) return false

  // 2) MIME 校验（部分浏览器对 xlsx/xls 返回 application/octet-stream，属正常情况，直接放行）
  const fileMime = (file.type || '').toLowerCase()
  if (!fileMime || fileMime === 'application/octet-stream') return true
  const allowMimes = FILE_MIME_MAP[ext]
  if (!allowMimes) return true // 未收录 MIME 映射的扩展名，仅按后缀放行
  return allowMimes.includes(fileMime)
}


export const messageBox = (msg: string) => {
  return new Promise((resolve, reject) => {
    ElMessageBox.confirm(t(msg), t('Warning'), {
      confirmButtonText: t('是的，确定'),
      cancelButtonText: t('不，取消'),
      type: 'warning',
      closeOnClickModal: false
    })
      .then(
        () => {
          resolve(void 0)
        },
        (e) => { }
      )
      .catch((e) => { })
  })
}

// 校验正整数（符合 async-validator）
export const validatePositiveInt = (rule, value, callback) => {
  if (value !== 0 && !value) {
    callback();
  } else if (!/^[1-9]\d*$/.test(value)) {
    callback(new Error(t('请输入大于0的整数')))
  } else {
    callback()
  }
}

// 校验数字
export const validateNumber = (rule, value, callback) => {
  if (value !== 0 && !value) {
    callback();
  } else if (!/^-?([1-9]\d*$|[1-9]\d*.\d+$|0\.\d+$)/.test(value)) {
    callback(new Error(t('请输入数字')))
  } else {
    callback()
  }
}

// 校验表单
export const formValidator = (formRef) => {
  if (!formRef) {
    console.error('There is no formRef!')
    return
  }
  return new Promise((resolve, reject) => {
    formRef.validate((v) => {
      if (v) {
        resolve(true)
      } else {
        reject('校验未通过')
      }
    })
  })
}

// 校验输入不能为空
export const validatorNonNull = (rule, value, callback, errorText, isRequired = true) => {
  if (!value && isRequired) {
    callback(new Error(t(errorText)))
  } else if (value && String(value).trim() === '') {
    callback(new Error(t('输入不能为空格')))
  } else {
    callback()
  }
}

// 补充小数位
export const addDecimal = (num, digits = 2) => {
  return Number(num).toFixed(digits)
}

// 精确到两位小数
export const floorNum = (str, num) => {
  let reg = new RegExp(`^[0-9]+(.[0-9]{1,${num}})?$`)
  return reg.test(str)
}

// 校验数字
export const numberValidator = (num) => {
  return (rule, value, callback) => {
    if (!floorNum(value, 2)) {
      callback(new Error(t('只允许输入数字')))
    }
    callback()
  }
}

// 校验输入是否url
export const validateUrl = (rule, value, callback) => {
  // const reg = /^((https|http|ftp|rtsp|mms)?:\/\/)[^\s]+/
  // const reg = /(https|http|ftp|rtsp|mms):\/\/([\w.]+\/?)\S*/
  const reg = /^((https|http|ftp|rtsp|mms):\/\/)?(([A-Za-z0-9]+-[A-Za-z0-9]+|[A-Za-z0-9]+)\.)+([A-Za-z]+)[/?:]?.*$/
  if (value == undefined || value === '') {
    callback(new Error(t('请输入URL')))
  } else if (!reg.test(value)) {
    callback(new Error(t('URL必须以 (https|http|ftp|rtsp|mms)://) 开头')))
  } else {
    callback()
  }
}

// 校验输入是否有效图片url
export const validateImageUrl = (rule, value, callback) => {
  // const reg = /^((https|http|ftp|rtsp|mms)?:\/\/)[^\s]+/
  // const reg = /(https|http|ftp|rtsp|mms):\/\/([\w.]+\/?)\S*/
  const reg = /^((https|http|ftp|rtsp|mms):\/\/)?(([A-Za-z0-9]+-[A-Za-z0-9]+|[A-Za-z0-9]+)\.)+([A-Za-z]+)[/?:]?.*$/
  if (value == undefined || value === '') {
    callback(new Error(t('请输入URL')))
  } else if (!reg.test(value)) {
    callback(new Error(t('URL必须以 (https|http|ftp|rtsp|mms)://) 开头')))
  } else {
    let newImg = new Image()
    newImg.src = value
    newImg.onerror = () => {
      callback(new Error(t('请输入有效地址')))
    }
    newImg.onload = () => {
      callback()
    }
  }
}